import { forwardRef, type ReactNode } from 'react';
import styled, { css } from 'styled-components';

import { Button } from '@hivebrite/design-system/Button';
import {
  type CATEGORIES,
  FontAwesomeIcon,
  type IconName,
} from '@hivebrite/design-system/FontAwesomeIcon';

import { useComponentContext } from 'hb-builder/hooks';

export interface ToolbarButtonProps {
  ariaLabel: string;
  children?: ReactNode;
  className?: string;
  iconName?: IconName;
  iconType?: CATEGORIES;
  isSelected?: boolean;
  onClick: (e: any) => void;
}

const ToolbarButton = forwardRef<HTMLDivElement, ToolbarButtonProps>(
  (
    { ariaLabel, children, className, iconName, iconType, isSelected, onClick },
    ref,
  ) => {
    const { type } = useComponentContext();
    const iconProp =
      iconName && iconType
        ? {
            icon: () => (
              <FontAwesomeIcon
                height="12px"
                iconType={iconType}
                iconName={iconName}
              />
            ),
          }
        : {};

    return (
      // className is needed to be able to target the element from a child's styles block
      <div data-testid="toolbar-button-wrapper" className={className}>
        <ToolbarButtonStyled
          aria-label={ariaLabel}
          onClick={onClick}
          // @ts-expect-error: ref type
          ref={ref}
          size="small"
          variant={isSelected ? 'primary' : 'inversed'}
          $isSelected={isSelected}
          $type={type}
          {...iconProp}
        >
          {children}
        </ToolbarButtonStyled>
      </div>
    );
  },
);

export default ToolbarButton;

interface ToolbarButtonStyledProps {
  $type: string;
  $isSelected?: boolean;
}

const ToolbarButtonStyled = styled(Button)<ToolbarButtonStyledProps>`
  min-height: 24px;
  width: 24px;
  padding: 0px !important;
  border-radius: 3px !important;

  ${({ $type, $isSelected, theme }) =>
    $type === 'section' &&
    css`
      box-shadow: none;
      color: ${$isSelected
        ? theme.colors.white
        : theme.colors.black} !important;
      &:hover {
        color: ${theme.colors.white} !important;
      }
    `};
  svg {
    pointer-events: none;
  }
`;
